import { useCallback } from 'react';
import { useQueryClient } from 'react-query';
import { AdaptoActions } from '@frontend/adapto-actions';
import { Actions } from '@frontend/contact-actions';
import { useCustomContactFormSlidePanel } from '@frontend/create-contact-panel';
import { i18next, useTranslation } from '@frontend/i18n';
import { sanitizePhoneNumber } from '@frontend/phone-numbers';
import { CallDetailsContext, RecentCallRecord } from '@frontend/phone-panels';
import { useContactPanelShallowStore } from '@frontend/shared';
import { useSlidePanelShallowStore } from '@frontend/slide-panel';
import { useAlert } from '@frontend/design-system';
import { queryKeys } from '../../query-keys';
import { getShortHandName } from './utils';

export type HistoryActionsGetterProps = {
  index: number;
  rowData: RecentCallRecord;
  isInsidePopoverDropdown?: boolean;
  addTag?: () => void;
};

export const useHistoryActionsGetter = ({
  searchQuery,
  downloadMedia,
  deleteMedia,
  downloadAccessMap,
  deleteAccessMap,
  onAfterAction = () => {},
}: {
  searchQuery: string;
  downloadMedia: (rowData: RecentCallRecord) => void;
  deleteMedia: (rowData: RecentCallRecord) => void;
  downloadAccessMap: Record<string, boolean>;
  deleteAccessMap: Record<string, boolean>;
  onAfterAction?: (rowData: RecentCallRecord, index: number) => void;
}): ((props: HistoryActionsGetterProps) => (React.ReactElement | null)[]) => {
  const { setPersonId } = useContactPanelShallowStore('setPersonId', 'personId');
  const { setShow } = useSlidePanelShallowStore<CallDetailsContext>('setShow');
  const queryClient = useQueryClient();

  const { t } = useTranslation('calls');
  const { setShow: openPanel } = useCustomContactFormSlidePanel();
  const alert = useAlert();

  return useCallback(
    ({ index, rowData, addTag, isInsidePopoverDropdown }: HistoryActionsGetterProps) =>
      getHistoryActions({
        rowData,
        isInsidePopoverDropdown,
        viewProfile: () => {
          if (!!rowData.person?.personId) {
            setPersonId(rowData.person?.personId);
            setShow(true, 'contact');
          }
        },
        createContact: () => {
          const number = rowData.direction === 'inbound' ? rowData.callerNumber : rowData.dialedNumber;

          const digitsOnly = number ? sanitizePhoneNumber(number) : '';
          const hasCountryCode = digitsOnly.startsWith('1');

          openPanel({
            show: true,
            context: {
              mode: 'create',
              person: {
                MobilePhone: hasCountryCode ? digitsOnly.slice(1) : digitsOnly,
              },
              locationId: rowData.locationId,
              onSave: () => {
                alert.success(t('Contact created successfully.'));

                // This line doesn't quite accomplish anything because the logs do not update with the newly created contact
                queryClient.invalidateQueries({ queryKey: queryKeys.recentCallLogs(searchQuery) });
                queryClient.invalidateQueries({
                  predicate: (query) => query.queryKey.includes('contacts'),
                });
              },
              onError: () => {
                alert.error(t('There was an error creating your contact. Please try again.'));
              },
            },
          });
        },
        addTag,
        downloadMedia: () => {
          downloadMedia(rowData);
        },
        deleteMedia: () => {
          deleteMedia(rowData);
        },
        hasDownloadAccess: rowData.locationId ? downloadAccessMap[rowData.locationId] : false,
        hasDeleteAccess: rowData.locationId ? deleteAccessMap[rowData.locationId] : false,
        onAfterAction: () => onAfterAction(rowData, index),
      }),
    [
      alert,
      deleteAccessMap,
      deleteMedia,
      downloadAccessMap,
      downloadMedia,
      onAfterAction,
      openPanel,
      queryClient,
      searchQuery,
      setPersonId,
      setShow,
      t,
    ]
  );
};

export const getHistoryActions = ({
  rowData,
  viewProfile,
  createContact,
  addTag,
  downloadMedia,
  deleteMedia,
  hasDownloadAccess,
  hasDeleteAccess,
  onAfterAction,
  isInsidePopoverDropdown,
}: {
  rowData: RecentCallRecord;
  viewProfile: () => void;
  createContact: () => void;
  isInsidePopoverDropdown?: boolean;
  addTag?: () => void;
  downloadMedia: () => void;
  deleteMedia: () => void;
  hasDownloadAccess: boolean;
  hasDeleteAccess: boolean;
  onAfterAction?: () => void;
}): (React.ReactElement | null)[] => {
  const { channelId, locationId, recordingPath, personId, direction, callerNumber, dialedNumber } = rowData;
  const hasMedia = !!(channelId && locationId && recordingPath);
  const showDownloadMedia = hasMedia && hasDownloadAccess;
  const showDeleteMedia = hasMedia && hasDeleteAccess;
  // For inbound calls, the contact is the caller (callerNumber)
  // For outbound calls, the contact is the person dialed (dialedNumber)
  const contactPhoneNumber = direction === 'inbound' ? callerNumber : dialedNumber;

  return [
    <Actions.Call
      label={`${i18next.t('Call')} ${getShortHandName(rowData)}`}
      context={{
        personId,
        phoneNumber: contactPhoneNumber,
      }}
      trackingId={`phone-portal-weave2.0-calls-btn-tablerowaction-call`}
      key='call-action'
      onClick={onAfterAction}
      isInsidePopoverDropdown={isInsidePopoverDropdown}
    />,
    <Actions.Message
      label={`${i18next.t('Message')} ${getShortHandName(rowData)}`}
      context={{
        personId,
        threadGroupId: locationId,
        personPhone: contactPhoneNumber,
      }}
      trackingIdSuffix={`phone-portal-weave2.0-calls-btn-tablerowaction-message`}
      key='message-action'
      onClick={onAfterAction}
    />,
    !!rowData.person?.personId ? (
      <AdaptoActions.Action
        icon='user'
        label={i18next.t('View Profile')}
        onClick={() => {
          viewProfile();
          onAfterAction?.();
        }}
        key='view-profile-action'
      />
    ) : (
      <AdaptoActions.Action
        icon='user-management'
        label={i18next.t('Create Contact')}
        onClick={() => {
          createContact();
          onAfterAction?.();
        }}
        key='create-contact-action'
      />
    ),
    addTag ? (
      <AdaptoActions.Action
        icon='label'
        label={i18next.t('Add Tag')}
        onClick={() => {
          addTag();
          onAfterAction?.();
        }}
        key='add-tag-action'
      />
    ) : null,
    showDownloadMedia ? (
      <AdaptoActions.Action
        icon='download-small'
        label={i18next.t('Download Recording')}
        onClick={() => {
          downloadMedia();
          onAfterAction?.();
        }}
        key='download-media-action'
      />
    ) : null,
    showDeleteMedia ? (
      <AdaptoActions.Action
        icon='trash-small'
        label={i18next.t('Delete Recording')}
        onClick={() => {
          deleteMedia();
          onAfterAction?.();
        }}
        destructive={true}
        key='delete-media-action'
      />
    ) : null,
  ];
};
